import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Card, Row, Col, Table, Form, Spinner, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import "../assets/css/dataTable.css";
import "../assets/css/padronSii.css";
import { AccountContext } from "components/Account/Account";
import ModalCargando from "components/Modal/ModalCargando";
import ModalError from "components/Modal/ModalError";
import downloadCSV from "../csv/downloadCSV";
import {
  obtenerPanel, obtenerPlanDetalle, obtenerPlanEncabezado, obtenerPlanPagina, buscarReceptor,
  crearJobDescarga, getJobDescarga,
  crearJobPlan, getJobPlan,
  crearJobAplicar, getJobAplicar,
  reanudarBreaker, setAutomatico,
} from "api/padronSii";

const AMBIENTES = [
  { valor: "palena", etiqueta: "Producción (palena)", boton: "Ejecutar incremental" },
  { valor: "maullin", etiqueta: "Certificación (maullin)", boton: "Certificación completa" },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_INTENTOS = 200; // ~16 minutos a 5s por consulta, igual que CheckDocumentosCargadosDesdeHasta

// En pantallas anchas el contenido se estiraba de borde a borde: cinco tarjetas
// de anchos distintos, el aviso desplegado a dos metros del botón que lo
// resuelve y media pantalla vacía. Se acota el ancho de lectura y se centra.
const ANCHO_MAXIMO = 1400;

// La escala es la del tema del monitor (light-bootstrap-dashboard-react.css):
// `.card label` para las etiquetas (12px mayúsculas #9A9A9A) y `.card-title`
// para los títulos (peso 300, #333). Los azules son la paleta corporativa de
// C2C (29-09-2026), la misma que define padronSii.css para botones y
// etiquetas; verde, ámbar y rojo quedan solo como semáforo.
const TEMA = {
  azul900: "#054A91",
  azul600: "#3E7CB1",
  azul300: "#81A4CD",
  azul100: "#DBE4EE",
  success: "#87CB16",
  warning: "#FF9500",
  danger: "#FF4A55",
  tinta: "#333333",
  suave: "#9A9A9A",
  borde: "#DDDDDD",
};

const ETIQUETA = {
  fontSize: "12px",
  textTransform: "uppercase",
  color: TEMA.suave,
  fontWeight: 400,
  letterSpacing: "0.02em",
  marginBottom: "2px",
};

const VALOR = { fontSize: "22px", fontWeight: 300, color: TEMA.tinta, lineHeight: 1.4 };
// Fila de acción de las tarjetas de controles: el mismo alto en las tres
// columnas aunque una tenga un botón grande y otra una etiqueta.
const FILA_ACCION = { gap: "8px", minHeight: "40px", margin: "4px 0 6px" };
const AYUDA = { fontSize: "12px", color: TEMA.suave, lineHeight: 1.5 };

// ---------------------------------------------------------------------------
// Botón y etiqueta: una sola forma para todo el panel. Antes convivían siete
// variantes de Bootstrap (outline, fill, sm y normal, verdes, celestes y
// grises) y en una misma fila no calzaban ni el alto ni el color. Alto, radio,
// peso y colores salen de padronSii.css.
// ---------------------------------------------------------------------------
function Boton({ tipo = "secundario", grande = false, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`pbtn pbtn--${tipo}${grande ? " pbtn--grande" : ""}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
}

// `ancho` fija el ancho para que una columna o una fila de etiquetas quede
// pareja aunque los textos midan distinto.
function Etiqueta({ tono = "azul", ancho, grande = false, title, children }) {
  return (
    <span
      className={`petq petq--${tono}${grande ? " petq--grande" : ""}`}
      style={ancho ? { width: ancho, minWidth: ancho } : undefined}
      title={title}
    >
      {children}
    </span>
  );
}

// Resultado de una ejecución -> tono de semáforo y un texto corto que entre en
// la columna. El valor crudo queda en el title.
const RESULTADO_ETIQUETA = {
  ok: { tono: "ok", texto: "ok" },
  error: { tono: "error", texto: "error" },
  bloqueado_guardas: { tono: "alerta", texto: "guardas" },
  bloqueado_breaker: { tono: "error", texto: "breaker" },
  csv_regresivo: { tono: "alerta", texto: "csv viejo" },
  pending: { tono: "neutro", texto: "pendiente" },
  running: { tono: "azul", texto: "corriendo" },
};

const ANCHO_ESTADO = 104;

function badgeResultado(resultado) {
  const e = RESULTADO_ETIQUETA[resultado] || { tono: "neutro", texto: resultado };
  return <Etiqueta tono={e.tono} ancho={ANCHO_ESTADO} title={resultado}>{e.texto}</Etiqueta>;
}

// Severidad del backend -> color del cuadro. Mismo vocabulario que usa el
// correo del parte diario.
//
// Con las clases `alert-*` del tema el aviso quedaba ilegible: fondo saturado
// con texto claro encima, y el botón de acción perdido dentro del mismo bloque
// de color. Paleta propia: fondo suave, franja de color a la izquierda y texto
// oscuro, que es como se leen los avisos en el resto del monitor.
const PALETA_AVISO = {
  critica: { fondo: "#FFEDEE", borde: TEMA.danger, texto: "#B3333C" },
  alta: { fondo: "#FFF4E5", borde: TEMA.warning, texto: "#A66100" },
  media: { fondo: "#EEF3F9", borde: TEMA.azul600, texto: TEMA.azul900 },
  info: { fondo: "#F4F5F7", borde: TEMA.suave, texto: "#666666" },
};

function estiloAviso(severidad) {
  const p = PALETA_AVISO[severidad] || PALETA_AVISO.info;
  return {
    background: p.fondo,
    borderLeft: `4px solid ${p.borde}`,
    color: p.texto,
    borderRadius: "0 6px 6px 0",
    padding: "12px 16px",
    marginBottom: "12px",
    // Para los avisos con botón (flex): separa el texto del botón. En Bootstrap
    // 4.6 no existe la clase gap-3 que se usaba antes.
    gap: "16px",
  };
}

// Avisos que hablan del plan pendiente y llevan su planId al lado.
const ACCION_SOBRE_PLAN = ["sin_aplicar", "guardas_rojas"];

// Qué mide cada guarda, en palabras. El `detalle` que manda el backend trae el
// número; esto explica por qué ese número importa, que es lo que no se entendía
// mirando "bajas: OK" a secas.
const QUE_MIDE_LA_GUARDA = {
  bajas: "Cuánta gente saldría del padrón de una sola vez. Un salto grande casi nunca son bajas reales: es un CSV incompleto.",
  cuarentena: "Cuántos receptores del CSV vienen sin una casilla de intercambio utilizable.",
  merma: "Si el CSV trae menos filas que la tabla. Detecta una descarga cortada a la mitad.",
  regresion: "Si el CSV parece más viejo que la tabla: no trae ninguna alta y aun así daría de baja o reactivaría gente. Es lo que pasó el 09-09-2026, cuando el SII sirvió el archivo de una semana antes.",
};

const num = (n) => (n == null ? "—" : Number(n).toLocaleString("es-CL"));
const fecha = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

// "2.219 altas · 372 bajas · 1.295 cambios", que es lo que hay que mirar antes
// de decidir si se aplica.
function resumenCorto(r) {
  if (!r) return "sin resumen";
  const partes = [`${num(r.altas)} altas`, `${num(r.bajas)} bajas`, `${num(r.cambios)} cambios`];
  if (r.reactivaciones) partes.push(`${num(r.reactivaciones)} reactivaciones`);
  return partes.join(" · ");
}

// ---------------------------------------------------------------------------
// Una tarjeta de estado. Todas del mismo alto y del mismo ancho: la fila de
// KPI se lee de un vistazo solo si son comparables entre sí.
// ---------------------------------------------------------------------------
function Kpi({ titulo, ayuda, valor, children }) {
  return (
    <Card className="h-100 mb-0">
      <Card.Body className="py-3">
        <div style={ETIQUETA} title={ayuda}>{titulo}</div>
        <div style={VALOR}>{valor}</div>
        {children}
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Qué hace el módulo y qué toca, dibujado. Tres actos: los dos primeros los
// dispara el cron y no escriben en la base; el tercero lo aprieta una persona y
// es el único que la modifica. Esa distinción es el corazón del diseño y hasta
// ahora había que contarla de memoria.
// ---------------------------------------------------------------------------
function DiagramaFlujo() {
  // Paleta corporativa: azul medio para lo que corre solo, azul profundo para
  // el acto humano (el mismo del botón «Aplicar»). El verde queda para el
  // semáforo, no para distinguir pasos.
  const AUTO = TEMA.azul600;
  const HUMANO = TEMA.azul900;
  const HUMANO_TEXTO = TEMA.azul900;
  const GRIS = TEMA.suave;
  const TINTA = TEMA.tinta;

  const paso = (x, color, colorTexto, nro, titulo, cuando) => (
    <g>
      <rect x={x} y="18" width="196" height="62" rx="8" fill="#fff" stroke={color} strokeWidth="2" />
      <circle cx={x + 22} cy="42" r="11" fill={color} />
      <text x={x + 22} y="46" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">{nro}</text>
      <text x={x + 40} y="46" fill={TINTA} fontSize="13.5" fontWeight="600">{titulo}</text>
      <text x={x + 40} y="65" fill={colorTexto} fontSize="11.5">{cuando}</text>
    </g>
  );

  const flecha = (x) => (
    <g>
      <line x1={x} y1="49" x2={x + 30} y2="49" stroke={GRIS} strokeWidth="1.5" />
      <polygon points={`${x + 30},49 ${x + 23},45 ${x + 23},53`} fill={GRIS} />
    </g>
  );

  const toca = (x, lineas) => (
    <g>
      {lineas.map((l, i) => (
        <text key={i} x={x} y={106 + i * 17} fill={i === 0 ? GRIS : TINTA} fontSize="11.5">{l}</text>
      ))}
    </g>
  );

  return (
    <svg viewBox="0 0 940 208" width="100%" style={{ maxWidth: 940, display: "block" }} role="img"
      aria-label="Flujo del módulo: descargar, planificar y aplicar">
      {paso(6, AUTO, AUTO, "1", "Descargar", "04:00 hora de Chile · automático")}
      {flecha(202)}
      {paso(238, AUTO, AUTO, "2", "Planificar", "04:20 hora de Chile · automático")}
      {flecha(434)}
      {paso(470, HUMANO, HUMANO_TEXTO, "3", "Aplicar", "solo si una persona lo aprieta")}

      {toca(6, ["toca:", "SII (una sesión, se cierra)", "S3: guarda el CSV + sha256"])}
      {toca(238, ["toca:", "S3: lee el CSV, escribe el plan", "Postgres: SOLO SELECT"])}
      {toca(470, ["escribe:", "padron_receptores_sii", "padron_cambios (changelog)"])}

      {/* La cuarta pieza no es un paso del pipeline: mira lo que quedó y avisa. */}
      <rect x="700" y="18" width="234" height="62" rx="8" fill="#fff" stroke={GRIS} strokeWidth="1.5" strokeDasharray="4 3" />
      <text x="716" y="42" fill={TINTA} fontSize="13.5" fontWeight="600">Parte diario</text>
      <text x="716" y="61" fill={GRIS} fontSize="11.5">08:50 hora de Chile · correo por SES</text>
      {toca(700, ["mira:", "el estado de arriba y avisa", "lo mismo que muestra este panel"])}

      <line x1="6" y1="178" x2="934" y2="178" stroke="#e3e3e3" strokeWidth="1" />
      <text x="6" y="196" fill={GRIS} fontSize="11.5">
        Nada se escribe en la base hasta el paso 3. Los pasos 1 y 2 se pueden repetir sin consecuencias.
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Las guardas, con lo que miden y lo que hicieron. Antes era una línea por
// guarda que decía "OK" y un número sin contexto.
// ---------------------------------------------------------------------------
function TablaGuardas({ guardas }) {
  if (!guardas || !guardas.length) return null;
  const hayRojas = guardas.some((g) => !g.ok);
  return (
    <div className="mb-3">
      <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
        Guardas — controles que se corren sobre el plan antes de dejarlo aplicar
      </div>
      {/* Lista y no tabla: el detalle de cada guarda es una frase con cifras y
          esto vive en la columna angosta del detalle. Cuatro columnas obligaban
          a cortar el texto justo donde está el número que importa. */}
      <div style={{ border: `1px solid ${TEMA.borde}`, borderRadius: "6px", overflow: "hidden" }}>
        {guardas.map((g, i) => (
          <div
            key={g.nombre}
            style={{
              padding: "9px 12px",
              borderTop: i === 0 ? "none" : `1px solid ${TEMA.borde}`,
              background: g.ok ? "transparent" : PALETA_AVISO.critica.fondo,
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <Etiqueta tono={g.ok ? "ok" : "error"} ancho={56}>{g.ok ? "pasó" : "rojo"}</Etiqueta>
              <span style={{ fontSize: "13px", fontWeight: 500, color: TEMA.tinta, textTransform: "capitalize" }}>{g.nombre}</span>
              <span style={{ fontSize: "12px", color: TEMA.suave }}>{g.ok ? "dejó seguir" : "bloqueó la aplicación"}</span>
            </div>
            <div style={{ fontSize: "12.5px", color: TEMA.tinta, marginTop: "3px" }}>{g.detalle}</div>
            <div style={{ ...AYUDA, marginTop: "2px" }}>{QUE_MIDE_LA_GUARDA[g.nombre] || ""}</div>
          </div>
        ))}
      </div>
      <div style={{ ...AYUDA, marginTop: "8px" }}>
        {hayRojas
          ? "Con una guarda en rojo el plan no se aplica: hay que revisarlo y, si de verdad corresponde, forzarlo a mano."
          : "Todas pasaron, así que este plan se puede aplicar por el camino normal."}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detalle del plan.
//
// Antes pedía el plan COMPLETO al abrirlo (0,1-2 MB por clic) para mostrar un
// resumen, las guardas y 20 cambios. En test hay planes con el padrón entero
// como altas (389 MB): la lambda moría por memoria y esto se quedaba girando
// para siempre, porque el error solo iba a la consola. Ahora se pide el
// encabezado (1 KB) y los registros por página, en un modal aparte.
// ---------------------------------------------------------------------------

const REGISTROS_POR_PAGINA = 25;

const LISTAS_PLAN = [
  { lista: "altas", titulo: "Altas" },
  { lista: "bajas", titulo: "Bajas" },
  { lista: "cambios", titulo: "Cambios" },
  { lista: "reactivaciones", titulo: "Reactivaciones" },
];

// Nombres de columna de padron_receptores_sii -> cómo se leen en pantalla.
const CAMPO_ETIQUETA = {
  mail_intercambio: "Casilla de intercambio",
  url: "URL",
  nro_resolucion: "N° resolución",
  fecha_resolucion: "Fecha resolución",
  razon_social: "Razón social",
  mail_origen_invalido: "Casilla original (inválida)",
  mail_motivo: "Motivo de la casilla",
  dv: "DV",
};

const megas = (bytes) => (bytes == null ? "—" : `${(bytes / 1048576).toLocaleString("es-CL", { maximumFractionDigits: 1 })} MB`);
const rutLegible = (rut, dv) => (rut == null ? "—" : `${Number(rut).toLocaleString("es-CL")}${dv ? `-${dv}` : ""}`);
const razonSocialDe = (item) => item?.razon_social ?? item?.fila?.razon_social ?? "";

// Un null en un campo del padrón es «el SII no informa nada», no la palabra null.
function ValorCampo({ valor }) {
  if (valor === null || valor === undefined || valor === "") {
    return <span style={{ color: TEMA.suave, fontStyle: "italic" }}>vacío</span>;
  }
  return <span style={{ wordBreak: "break-word" }}>{String(valor)}</span>;
}

// Qué es este plan HOY, mirando el índice (el más reciente primero):
//   aplicado     -> ya se escribió en la base.
//   reemplazado  -> después se aplicó otro, o hay uno más nuevo. Aplicarlo no
//                   sirve: cada plan es la foto completa contra la base, así que
//                   el más nuevo ya incluye todo lo de éste. Si además se aplicó
//                   otro después, la base lo rechazaría como PLAN AÑEJO.
//   pendiente    -> es el último plan y nadie aplicó nada después. El único que
//                   corresponde aplicar.
// El 24-09-2026 alguien apretó «Aplicar» sobre un plan ya aplicado: el botón
// estaba ahí para cualquier plan, aplicado o no.
function estadoDelPlan(planId, indice = []) {
  const ok = (x) => x.resultado === "ok";
  const aplicacion = indice.find((x) => x.tipo === "aplicar" && ok(x) && x.planId === planId);
  if (aplicacion) return { tipo: "aplicado", at: aplicacion.terminadoAt };

  const entradaPlan = indice.find((x) => x.tipo === "plan" && ok(x) && x.planId === planId);
  const generado = entradaPlan?.terminadoAt || null;
  const aplicacionPosterior = generado
    && indice.find((x) => x.tipo === "aplicar" && ok(x) && (x.terminadoAt || "") > generado);
  if (aplicacionPosterior) return { tipo: "reemplazado", motivo: "aplicacion", entrada: aplicacionPosterior };

  const ultimoPlan = indice.find((x) => x.tipo === "plan" && ok(x) && x.planId);
  if (ultimoPlan && ultimoPlan.planId !== planId) return { tipo: "reemplazado", motivo: "plan", entrada: ultimoPlan };

  return { tipo: "pendiente" };
}

const ESTADO_PLAN_ETIQUETA = {
  aplicado: { tono: "ok", texto: "aplicado" },
  pendiente: { tono: "alerta", texto: "sin aplicar" },
  reemplazado: { tono: "neutro", texto: "reemplazado" },
};

function etiquetaEstadoPlan(tipo, title) {
  const e = ESTADO_PLAN_ETIQUETA[tipo];
  return e ? <Etiqueta tono={e.tono} ancho={ANCHO_ESTADO} title={title}>{e.texto}</Etiqueta> : null;
}

// Mientras el backend nuevo no esté desplegado, `?vista=encabezado` devuelve el
// plan entero: se reduce acá a lo mismo que mandaría el backend nuevo.
function comoEncabezado(data) {
  if (!data || data.totales) return data;
  const salida = { demasiadoGrande: false, totales: {} };
  for (const [clave, valor] of Object.entries(data)) {
    if (Array.isArray(valor) && clave !== "guardas") salida.totales[clave] = valor.length;
    else salida[clave] = valor;
  }
  return salida;
}

// Las cuatro cifras que deciden si se aplica, del mismo tamaño y alineadas.
// Cada una abre sus registros.
function CifrasPlan({ totales, onAbrir }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
      {LISTAS_PLAN.map(({ lista, titulo }) => {
        const n = totales?.[lista];
        const clickeable = onAbrir && n > 0;
        return (
          <button
            key={lista}
            type="button"
            className="pcifra"
            onClick={clickeable ? () => onAbrir(lista) : undefined}
            disabled={!clickeable}
            title={clickeable ? `Ver las ${titulo.toLowerCase()} una por una` : undefined}
          >
            <div style={{ ...ETIQUETA, fontSize: "11px" }}>{titulo}</div>
            <div style={{ fontSize: "20px", fontWeight: 300, color: n ? TEMA.azul900 : TEMA.suave, fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}>
              {num(n)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DetallePlan({
  fila, encabezado, cargando, error, estado, breakerAbierto, ocupado,
  onReintentar, onAplicar, onVerRegistros, onExportar, onIrAPlan,
}) {
  if (!fila) {
    return (
      <p className="mb-0" style={AYUDA}>
        Elegí una ejecución del historial. Si es un plan, acá aparece qué cambiaría,
        qué dijeron las guardas y, si corresponde, el botón para aplicarlo.
      </p>
    );
  }

  if (!fila.planId) {
    const r = fila.resumen || {};
    return (
      <div style={AYUDA}>
        <div className="mb-1" style={{ color: TEMA.tinta, fontSize: "13px" }}>
          {fila.tipo === "descargar" ? "Descarga" : fila.tipo} del {fecha(fila.terminadoAt || fila.iniciadoAt)} · {badgeResultado(fila.resultado)}
        </div>
        {r.sha256 && <div>CSV de {megas(r.bytes)} · sha256 <span className="text-monospace">{r.sha256.slice(0, 12)}…</span></div>}
        Esta ejecución no generó un plan: fue una descarga, o terminó antes de planificar.
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="d-flex align-items-center" style={{ gap: "8px", ...AYUDA }}>
        <Spinner animation="border" size="sm" /> Cargando el plan…
      </div>
    );
  }

  if (error) {
    return (
      <div style={estiloAviso("critica")}>
        <strong>No se pudo abrir el plan</strong>
        <div className="small">{error}</div>
        <div className="mt-2"><Boton tipo="peligro" onClick={onReintentar}>Reintentar</Boton></div>
      </div>
    );
  }

  if (!encabezado) return null;

  const t = encabezado.totales || {};
  const hayRojas = (encabezado.guardas || []).some((g) => !g.ok);
  const vigentesAntes = encabezado.operativa?.vigentes ?? encabezado.resumen?.vigentesActuales;
  const vigentesDespues = encabezado.checksum_despues_esperado?.filas;
  const delta = vigentesAntes != null && vigentesDespues != null ? Number(vigentesDespues) - Number(vigentesAntes) : null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1" style={{ gap: "8px" }}>
        <span className="text-monospace" style={{ fontSize: "12.5px", color: TEMA.azul900, wordBreak: "break-all" }}>{encabezado.plan_id}</span>
        <span className="flex-shrink-0">{etiquetaEstadoPlan(estado.tipo)}</span>
      </div>
      <div className="mb-3" style={AYUDA}>
        <div>generado {fecha(encabezado.generado_at)}{encabezado.bytes != null && <> · plan de {megas(encabezado.bytes)}</>}</div>
        {encabezado.csv?.archivo && <div className="text-truncate" title={encabezado.csv.archivo}>{encabezado.csv.archivo}</div>}
      </div>

      <CifrasPlan totales={t} onAbrir={encabezado.demasiadoGrande ? null : onVerRegistros} />

      <div className="mt-2 mb-3" style={AYUDA}>
        {vigentesAntes != null && vigentesDespues != null && (
          <>vigentes {num(vigentesAntes)} → {num(vigentesDespues)} ({delta >= 0 ? "+" : ""}{num(delta)}) · </>
        )}
        sin cambio {num(encabezado.resumen?.sin_cambio)}
        {t.congelados > 0 && <> · <span title="Filas que el plan deja intactas por tener un override manual.">{num(t.congelados)} congelados</span></>}
      </div>

      {encabezado.demasiadoGrande && (
        <div style={estiloAviso("alta")}>
          <strong>Plan demasiado grande para abrirlo acá</strong>
          <div className="small">
            Pesa {megas(encabezado.bytes)} (el panel abre hasta {megas(encabezado.limiteBytes)}). Un plan así trae
            el padrón completo como altas: no es un incremental del día, es una carga desde cero. Las cifras y las
            guardas salen del registro de la ejecución; los registros uno por uno no se abren desde el panel.
          </div>
        </div>
      )}

      <TablaGuardas guardas={encabezado.guardas} />

      {/* Qué se puede hacer con este plan, según su estado real. */}
      {estado.tipo === "aplicado" && (
        <div className="mb-3" style={AYUDA}>
          Se aplicó el {fecha(estado.at)}. Ya está en la base: no hay nada más que hacer con él.
        </div>
      )}
      {estado.tipo === "reemplazado" && (
        <div className="mb-3" style={estiloAviso("info")}>
          {estado.motivo === "aplicacion" ? (
            <div className="small">
              Después de este plan se aplicó otro ({fecha(estado.entrada.terminadoAt)}). La base ya no está como
              cuando se generó, así que aplicarlo lo rechazaría como <strong>PLAN AÑEJO</strong>. No se perdió nada:
              cada plan compara la base contra el padrón completo del SII, y el que se aplicó ya lo incluye.
            </div>
          ) : (
            <div className="small">
              Hay un plan más nuevo ({fecha(estado.entrada.terminadoAt)}). Cada plan es la foto completa del padrón
              contra la base, así que el más nuevo ya trae todo lo de éste: se aplica ése.
              <div className="mt-2">
                <Boton onClick={() => onIrAPlan(estado.entrada)}>Ver el plan más nuevo</Boton>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
        {estado.tipo === "pendiente" && (
          <>
            <Boton
              tipo="primario"
              onClick={() => onAplicar(encabezado.plan_id, false)}
              disabled={ocupado || breakerAbierto || hayRojas}
              title={hayRojas ? "Hay guardas en rojo: el camino normal está bloqueado." : undefined}
            >
              Aplicar
            </Boton>
            {hayRojas && (
              <Boton tipo="peligro" onClick={() => onAplicar(encabezado.plan_id, true)} disabled={ocupado || breakerAbierto}>
                Forzar de todos modos
              </Boton>
            )}
          </>
        )}
        <Boton onClick={() => onVerRegistros(null)} disabled={encabezado.demasiadoGrande}>Ver registros</Boton>
        <Boton onClick={onExportar} disabled={encabezado.demasiadoGrande || ocupado}>Exportar CSV</Boton>
      </div>
      {estado.tipo === "pendiente" && (
        <div className="mt-2" style={AYUDA}>
          Aplicar escribe en <span className="text-monospace">padron_receptores_sii</span> en una sola transacción.
          Si al terminar la tabla no cuadra con el CSV, se deshace entera. Las bajas no borran filas: las marcan.
        </div>
      )}
    </>
  );
}

// Columnas de cada lista. Los cambios van una fila por campo, con el RUT solo
// en la primera: así se lee «esta empresa cambió casilla y URL» de un vistazo.
function FilasRegistros({ lista, items }) {
  const td = { padding: "7px 10px", verticalAlign: "top", borderTop: `1px solid ${TEMA.borde}` };
  const rutTd = { ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

  if (lista === "cambios") {
    return items.flatMap((c) => (c.campos || []).map((x, i) => (
      <tr key={`${c.rut}-${x.campo}`}>
        {i === 0 && <td style={rutTd} rowSpan={c.campos.length}>{rutLegible(c.rut)}</td>}
        {i === 0 && <td style={td} rowSpan={c.campos.length}>{razonSocialDe(c) || <ValorCampo valor={null} />}</td>}
        <td style={{ ...td, color: TEMA.suave, whiteSpace: "nowrap" }}>{CAMPO_ETIQUETA[x.campo] || x.campo}</td>
        <td style={{ ...td, color: TEMA.suave }}><ValorCampo valor={x.anterior} /></td>
        <td style={{ ...td, color: TEMA.tinta, fontWeight: 500 }}><ValorCampo valor={x.nuevo} /></td>
      </tr>
    )));
  }

  return items.map((f) => {
    const datos = f.fila || f;
    return (
      <tr key={f.rut}>
        <td style={rutTd}>{rutLegible(f.rut, datos.dv)}</td>
        <td style={td}>{razonSocialDe(f) || <ValorCampo valor={null} />}</td>
        {lista !== "bajas" && (
          <td style={td}>
            {datos.mail_intercambio
              ? <ValorCampo valor={datos.mail_intercambio} />
              : <span style={{ color: TEMA.suave, fontStyle: "italic" }}>sin casilla{datos.mail_motivo ? ` (${datos.mail_motivo})` : ""}</span>}
          </td>
        )}
        {lista === "altas" && (
          <td style={{ ...td, whiteSpace: "nowrap", color: TEMA.suave }}>
            {datos.nro_resolucion ?? "—"}{datos.fecha_resolucion ? ` · ${datos.fecha_resolucion}` : ""}
          </td>
        )}
        {lista === "reactivaciones" && (
          <td style={{ ...td, color: TEMA.suave }}>
            {(f.campos || []).length
              ? f.campos.map((x) => `${CAMPO_ETIQUETA[x.campo] || x.campo}: ${x.anterior ?? "vacío"} → ${x.nuevo ?? "vacío"}`).join(" · ")
              : "vuelve igual que antes de la baja"}
          </td>
        )}
      </tr>
    );
  });
}

const ENCABEZADOS_LISTA = {
  altas: ["RUT", "Razón social", "Casilla de intercambio", "Resolución"],
  bajas: ["RUT", "Razón social"],
  cambios: ["RUT", "Razón social", "Campo", "Antes", "Ahora"],
  reactivaciones: ["RUT", "Razón social", "Casilla de intercambio", "Qué cambia"],
};

// Los registros del plan, por página y con búsqueda, en un modal: la columna del
// detalle es angosta y una casilla de correo larga no entraba sin romper la fila.
function RegistrosPlan({ show, onHide, getSession, planId, totales, listaInicial, onExportar, exportando }) {
  const [lista, setLista] = useState(listaInicial || "cambios");
  const [desde, setDesde] = useState(0);
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [pagina, setPagina] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const ultimaConsulta = useRef(0);

  useEffect(() => {
    if (show) { setLista(listaInicial || "cambios"); setDesde(0); setTexto(""); setQ(""); setPagina(null); }
  }, [show, listaInicial, planId]);

  // La búsqueda sale al dejar de escribir, no con cada tecla.
  useEffect(() => {
    const t = setTimeout(() => { setQ(texto.trim()); setDesde(0); }, 350);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    if (!show || !planId) return;
    const consulta = ++ultimaConsulta.current;
    setCargando(true);
    setError(null);
    (async () => {
      try {
        const idToken = await getSession().then((s) => s.idToken.jwtToken);
        const resp = await obtenerPlanPagina(idToken, planId, { lista, desde, cantidad: REGISTROS_POR_PAGINA, q });
        if (consulta !== ultimaConsulta.current) return; // llegó tarde: ya se pidió otra cosa
        let datos = resp.data;
        // Backend viejo: devuelve el plan entero. Se pagina acá, sin búsqueda.
        if (!Array.isArray(datos.items)) {
          const todos = Array.isArray(datos[lista]) ? datos[lista] : [];
          datos = { lista, total: todos.length, totalFiltrado: todos.length, desde, cantidad: REGISTROS_POR_PAGINA, items: todos.slice(desde, desde + REGISTROS_POR_PAGINA) };
        }
        setPagina(datos);
      } catch (e) {
        if (consulta !== ultimaConsulta.current) return;
        setError(e?.response?.data?.error || e.message || "Error inesperado.");
      } finally {
        if (consulta === ultimaConsulta.current) setCargando(false);
      }
    })();
  }, [show, planId, lista, desde, q, getSession]);

  const cambiarLista = (l) => { setLista(l); setDesde(0); };
  const hasta = pagina ? Math.min(pagina.desde + pagina.items.length, pagina.totalFiltrado) : 0;
  const th = { padding: "8px 10px", fontSize: "11.5px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.03em", textAlign: "left", whiteSpace: "nowrap" };

  return (
    // El modal se monta fuera de la vista: lleva la clase de la paleta él mismo.
    <Modal show={show} onHide={onHide} size="xl" scrollable centered className="padron-sii">
      <Modal.Header closeButton>
        <Modal.Title as="h5" style={{ fontWeight: 300 }}>
          Registros del plan <span className="text-monospace" style={{ fontSize: "13px", color: TEMA.azul600 }}>{planId}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: "10px" }}>
          <div className="psegmentos" role="group" aria-label="Qué registros ver">
            {LISTAS_PLAN.map(({ lista: l, titulo }) => (
              <button
                key={l} type="button"
                className={`psegmento${lista === l ? " psegmento--activo" : ""}`}
                onClick={() => cambiarLista(l)}
              >
                {titulo} <span className="psegmento__n">{num(totales?.[l] ?? 0)}</span>
              </button>
            ))}
          </div>
          <Form.Control
            type="text" style={{ maxWidth: 340, height: 34 }}
            placeholder="Buscar por RUT, razón social o casilla…"
            value={texto} onChange={(e) => setTexto(e.target.value)}
          />
        </div>

        {error && <div style={estiloAviso("critica")}><strong>No se pudieron traer los registros</strong><div className="small">{error}</div></div>}

        <div style={{ border: `1px solid ${TEMA.azul100}`, borderRadius: "8px", overflow: "hidden", opacity: cargando ? 0.55 : 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "auto" }}>
            <thead>
              <tr style={{ background: TEMA.azul900, color: "#fff" }}>
                {ENCABEZADOS_LISTA[lista].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pagina && pagina.items.length > 0 && <FilasRegistros lista={lista} items={pagina.items} />}
              {pagina && pagina.items.length === 0 && (
                <tr><td colSpan={ENCABEZADOS_LISTA[lista].length} style={{ padding: "18px 10px", ...AYUDA, textAlign: "center" }}>
                  {q ? `Nada coincide con «${q}».` : "Este plan no trae registros de este tipo."}
                </td></tr>
              )}
              {!pagina && (
                <tr><td colSpan={ENCABEZADOS_LISTA[lista].length} style={{ padding: "18px 10px", ...AYUDA, textAlign: "center" }}>
                  <Spinner animation="border" size="sm" /> Cargando…
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2" style={{ gap: "10px" }}>
          <div style={AYUDA}>
            {pagina && pagina.totalFiltrado > 0 && (
              <>
                {num(pagina.desde + 1)}–{num(hasta)} de {num(pagina.totalFiltrado)}
                {q && pagina.totalFiltrado !== pagina.total && <> (de {num(pagina.total)} en total)</>}
              </>
            )}
          </div>
          <div className="d-flex" style={{ gap: "8px" }}>
            <Boton disabled={cargando || !pagina || desde === 0}
              onClick={() => setDesde(Math.max(desde - REGISTROS_POR_PAGINA, 0))}>‹ Anterior</Boton>
            <Boton disabled={cargando || !pagina || hasta >= pagina.totalFiltrado}
              onClick={() => setDesde(desde + REGISTROS_POR_PAGINA)}>Siguiente ›</Boton>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span style={AYUDA}>El CSV trae todo el plan: altas, bajas, reactivaciones y cada campo con su valor anterior y el nuevo.</span>
        <div className="d-flex" style={{ gap: "8px" }}>
          <Boton onClick={onExportar} disabled={exportando}>{exportando ? "Preparando…" : "Exportar CSV"}</Boton>
          <Boton tipo="primario" onClick={onHide}>Cerrar</Boton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function PadronSii() {
  const { getSession } = useContext(AccountContext);

  const [ambiente, setAmbiente] = useState("palena");
  const [panel, setPanel] = useState(null);
  const [cargandoPanel, setCargandoPanel] = useState(false);

  const [filter, setFilter] = useState("");
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [planDetalle, setPlanDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [registros, setRegistros] = useState({ show: false, lista: null });
  const [exportando, setExportando] = useState(false);
  // Cada clic en el historial invalida las respuestas de los clics anteriores:
  // sin esto, un plan pesado pedido antes podía llegar después y pisar al que
  // se estaba mirando — con su botón «Aplicar».
  const ultimaSeleccion = useRef(0);
  const [verComoFunciona, setVerComoFunciona] = useState(false);

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const cfgAmbiente = useMemo(() => AMBIENTES.find((a) => a.valor === ambiente), [ambiente]);

  // Dos banderas independientes, no una:
  //   cronActivo      -> interruptor del operador; solo gobierna la corrida diaria.
  //   breakerAbierto  -> reacción a 3 errores; bloquea el cron Y lo manual.
  // Los estados guardados antes del 07-09-2026 no traen `cron_activo`; ausente
  // se lee como prendido, que es como se comportaban.
  const breaker = panel?.breaker;
  const cronActivo = breaker ? breaker.cron_activo !== false : true;
  const breakerAbierto = breaker?.modo === "manual";
  const apagadoPor = breaker?.cron_cambiado_by
    ? `${breaker.cron_cambiado_by}${breaker.cron_cambiado_at ? ` el ${breaker.cron_cambiado_at.replace("T", " ").slice(0, 16)}` : ""}`
    : null;

  // Lo pendiente lo calcula el backend (GET /padronSii/panel), no esta vista:
  // la misma regla la va a usar la alerta por correo.
  const pendiente = panel?.pendiente;
  const avisos = panel?.avisos || [];

  // Olvida el plan que se estaba mirando y descarta cualquier respuesta que
  // todavía esté en camino.
  const limpiarSeleccion = useCallback(() => {
    ultimaSeleccion.current += 1;
    setFilaSeleccionada(null);
    setPlanDetalle(null);
    setErrorDetalle(null);
    setCargandoDetalle(false);
  }, []);

  const cargarPanel = useCallback(async (amb) => {
    setCargandoPanel(true);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPanel(idToken, amb);
      setPanel(resp.data);
    } catch (error) {
      console.error(error);
    } finally {
      setCargandoPanel(false);
    }
  }, [getSession]);

  useEffect(() => { cargarPanel(ambiente); limpiarSeleccion(); }, [ambiente, cargarPanel, limpiarSeleccion]);

  // Polling genérico: espera a que un job deje de estar pending/running.
  const esperarJob = async (idToken, getJobFn, jobId, mensajeFn) => {
    for (let i = 0; i < MAX_INTENTOS; i++) {
      await sleep(5000);
      const resp = await getJobFn(idToken, jobId);
      const job = resp.data;
      if (!["pending", "running"].includes(job.status)) return job;
      if (mensajeFn) setLoadingMessage(mensajeFn(job));
    }
    throw new Error("Tiempo de espera agotado.");
  };

  // "Ejecutar incremental" / "Certificación completa": descargar -> plan,
  // orquestado desde el frontend (no una lambda invocando a la otra).
  const ejecutarIncremental = async () => {
    setShowLoading(true);
    setLoadingMessage(" Iniciando descarga...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);

      const jobDescarga = await crearJobDescarga(idToken, { ambiente });
      const resultDescarga = await esperarJob(idToken, getJobDescarga, jobDescarga.data.jobId,
        () => " Descargando el padrón del SII...");
      if (resultDescarga.status === "bloqueado_breaker") {
        throw new Error(resultDescarga.error || "Bloqueado por el circuit breaker.");
      }
      if (resultDescarga.status !== "done") {
        throw new Error(resultDescarga.error || "La descarga terminó con error.");
      }

      setLoadingMessage(" Planificando (diff contra la base)...");
      const jobPlan = await crearJobPlan(idToken, { ambiente, csvKey: resultDescarga.resultado.csvKey });
      const resultPlan = await esperarJob(idToken, getJobPlan, jobPlan.data.jobId,
        () => " Planificando (diff contra la base)...");
      if (resultPlan.status === "error") throw new Error(resultPlan.error || "El plan terminó con error.");

      setShowLoading(false);
      await cargarPanel(ambiente);
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const aplicarPlan = async (planId, forzar = false) => {
    setShowLoading(true);
    setLoadingMessage(" Aplicando el plan...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const job = await crearJobAplicar(idToken, { planId, forzar });
      const resultado = await esperarJob(idToken, getJobAplicar, job.data.jobId, () => " Aplicando el plan...");
      if (resultado.status === "error") throw new Error(resultado.error || "Error al aplicar.");
      setShowLoading(false);
      if (resultado.status === "bloqueado_guardas" && !forzar) {
        setErrorMessage("Hay guardas en rojo. Revisar el detalle antes de forzar la aplicación.");
        setShowErrorModal(true);
      }
      await cargarPanel(ambiente);
      limpiarSeleccion();
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const onReanudar = async () => {
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await reanudarBreaker(idToken, { ambiente });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al reanudar.");
      setShowErrorModal(true);
    }
  };

  // Prende/apaga SOLO la corrida diaria. Apagarla no toca el circuit breaker ni
  // deshabilita el botón "Ejecutar incremental": el sentido de apagarla es
  // decidir uno cuándo corre.
  const onToggleAutomatico = async () => {
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await setAutomatico(idToken, { ambiente, activo: !cronActivo });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al cambiar la corrida automática.");
      setShowErrorModal(true);
    }
  };

  const seleccionarFila = async (fila) => {
    const seleccion = ++ultimaSeleccion.current;
    setFilaSeleccionada(fila);
    setPlanDetalle(null);
    setErrorDetalle(null);
    if (!fila?.planId) { setCargandoDetalle(false); return; }
    setCargandoDetalle(true);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPlanEncabezado(idToken, fila.planId);
      if (seleccion !== ultimaSeleccion.current) return;
      setPlanDetalle(comoEncabezado(resp.data));
    } catch (error) {
      if (seleccion !== ultimaSeleccion.current) return;
      console.error(error);
      // Antes el error iba solo a la consola y el spinner quedaba girando.
      setErrorDetalle(error?.response?.data?.error
        || (error?.response?.status ? `El servidor respondió ${error.response.status}.` : "No hubo respuesta del servidor."));
    } finally {
      if (seleccion === ultimaSeleccion.current) setCargandoDetalle(false);
    }
  };

  // CSV para revisar el plan afuera antes de aplicarlo: un renglón por cada
  // cambio concreto, con el valor anterior y el nuevo al lado. Es lo único que
  // todavía pide el plan completo, y solo cuando se aprieta el botón.
  //
  // Todas las filas llevan las mismas columnas a propósito: downloadCSV arma el
  // encabezado con las claves de la PRIMERA fila, así que mientras el detalle de
  // los cambios tuvo una columna propia bastaba con que hubiera un alta arriba
  // para que ese dato no llegara nunca al archivo.
  const exportarDetalleCSV = async () => {
    if (!planDetalle) return;
    setExportando(true);
    let plan;
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      plan = (await obtenerPlanDetalle(idToken, planDetalle.plan_id)).data;
    } catch (error) {
      setErrorMessage(error?.response?.data?.error || error.message || "No se pudo bajar el plan.");
      setShowErrorModal(true);
      return;
    } finally {
      setExportando(false);
    }
    const fila = (accion, f, campo, anterior, nuevo) => ({
      plan_id: plan.plan_id,
      accion,
      rut: f.rut ?? "",
      // Las reactivaciones guardan la fila entrante anidada en `fila`, y los
      // planes anteriores al 08-09-2026 no traen razón social en los cambios:
      // en ese caso la columna queda vacía, como venía.
      razon_social: f.razon_social ?? f.fila?.razon_social ?? "",
      campo: campo ?? "",
      anterior: anterior ?? "",
      nuevo: nuevo ?? "",
    });
    const filas = [
      ...(plan.altas || []).map((f) => fila("alta", f)),
      ...(plan.bajas || []).map((f) => fila("baja", f)),
      ...(plan.reactivaciones || []).map((f) => fila("reactivacion", f)),
      ...(plan.cambios || []).flatMap((c) =>
        (c.campos || []).map((x) => fila("cambio", c, x.campo, x.anterior, x.nuevo))),
    ];
    if (!filas.length) return;
    downloadCSV(filas, `padron_${plan.plan_id}.csv`);
  };

  const estadoPlanSeleccionado = useMemo(
    () => (planDetalle ? estadoDelPlan(planDetalle.plan_id, panel?.indice || []) : null),
    [planDetalle, panel]);

  const indiceFiltrado = useMemo(() => {
    const indice = panel?.indice || [];
    if (!filter) return indice;
    const f = filter.toLowerCase();
    return indice.filter((x) =>
      (x.tipo || "").toLowerCase().includes(f) ||
      (x.resultado || "").toLowerCase().includes(f) ||
      (x.planId || "").toLowerCase().includes(f));
  }, [panel, filter]);

  // Vigentes: no hay un endpoint de conteo aparte — se toma del último job
  // (plan o aplicar) que trajo un número, que ya viaja en el índice. Un plan
  // sin aplicar todavía trae vigentesActuales (lo que había ANTES de ese
  // plan); un aplicar trae filasVigentes (lo que quedó DESPUÉS). El más
  // reciente de los dos es la mejor estimación disponible sin agregar una
  // consulta nueva a la base solo para esto.
  const vigentes = useMemo(() => {
    const conDato = (panel?.indice || []).find((x) => x.resumen && (x.resumen.filasVigentes ?? x.resumen.vigentesActuales) != null);
    if (!conDato) return null;
    return conDato.resumen.filasVigentes ?? conDato.resumen.vigentesActuales;
  }, [panel]);

  // Altas/bajas/cambios separados en vez de una suma: la suma escondía justo lo
  // que se necesita para decidir (300 bajas no es lo mismo que 300 altas).
  // Los anchos suman lo que entra en la columna del historial (7/12 de la
  // pantalla): antes sumaban ~690px y la tabla se salía de su tarjeta con una
  // barra de scroll horizontal. Los segundos de la fecha no le sirven a nadie.
  const columnas = [
    // Sin `wrap`: la fecha completa entra en un renglon y partirla en dos
    // ("2026-09-09" arriba, "11:20" abajo) hacia la tabla mas alta y mas
    // dificil de recorrer con la vista.
    { name: "Fecha", selector: (row) => row.terminadoAt || row.iniciadoAt, sortable: true, minWidth: "140px",
      style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
      cell: (row) => fecha(row.terminadoAt || row.iniciadoAt) },
    // Una etiqueta por columna y del mismo ancho en todas las filas: el tipo en
    // azul (es información, no semáforo) y el estado en el semáforo.
    { name: "Tipo", selector: (row) => row.tipo, sortable: true, minWidth: "112px",
      cell: (row) => <Etiqueta tono="azul" ancho={84}>{row.tipo}</Etiqueta> },
    // Los anchos fijos de 80px partían los números en dos renglones ("1 748.0
    // 00") apenas el padrón completo entraba como altas. Se dan por contenido,
    // sin cortar, y alineados a la derecha para poder compararlos de un vistazo.
    { name: "Altas", right: true, minWidth: "82px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.altas) },
    { name: "Bajas", right: true, minWidth: "72px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.bajas) },
    { name: "Cambios", right: true, minWidth: "82px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.cambios) },
    // Para un plan que salió bien importa si se aplicó, no que «ok»: «sin
    // aplicar» solo en el que de verdad está pendiente; los viejos que nadie
    // aplicó porque vino uno más nuevo quedan como «reemplazado». El resto de
    // las filas muestra el resultado de la ejecución.
    { name: "Estado", minWidth: "128px",
      cell: (row) => (row.tipo === "plan" && row.planId && row.resultado === "ok"
        ? etiquetaEstadoPlan(estadoDelPlan(row.planId, panel?.indice || []).tipo, "resultado: ok")
        : badgeResultado(row.resultado)) },
  ];

  const estadoGeneral = breakerAbierto
    ? { texto: "Bloqueado", tono: "error", nota: "el circuit breaker está abierto" }
    : cronActivo
      ? { texto: "En automático", tono: "ok", nota: "el cron descarga y planifica solo" }
      : { texto: "Solo manual", tono: "neutro", nota: "el cron no dispara; el botón sí funciona" };

  return (
    <>
      <div className="padron-sii" style={{ maxWidth: ANCHO_MAXIMO, margin: "0 auto" }}>
        {/* ---- Encabezado: qué es esto, y sobre qué ambiente estoy mirando ---- */}
        <Card className="mb-3">
          <Card.Body className="py-3">
            <Row className="align-items-center g-2">
              <Col md="7">
                <Card.Title as="h4" className="mb-1">Padrón SII</Card.Title>
                <div style={{ fontSize: "13px", color: TEMA.suave, lineHeight: 1.55 }}>
                  Nómina oficial de receptores electrónicos del SII. De acá sale la casilla
                  a la que se le manda cada factura, así que el padrón viejo es factura mal enviada.
                </div>
              </Col>
              <Col md="5">
                <div className="d-flex align-items-center justify-content-md-end" style={{ gap: "10px" }}>
                  <span className="text-muted small flex-shrink-0">Ambiente</span>
                  <Form.Select
                    value={ambiente}
                    onChange={(e) => setAmbiente(e.target.value)}
                    style={{ maxWidth: 260 }}
                  >
                    {AMBIENTES.map((a) => <option key={a.valor} value={a.valor}>{a.etiqueta}</option>)}
                  </Form.Select>
                </div>
                <div className="text-md-right mt-1" style={AYUDA}>
                  {cargandoPanel
                    ? "cargando…"
                    : panel
                      ? <>base <strong>{ambiente}</strong> · última descarga {fecha(panel.ultimaDescarga?.descargado_at)}</>
                      : ""}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ---- Avisos. Los evalúa el backend, y son exactamente los mismos que
             sale a decir el parte diario por correo: así el panel y el correo no
             pueden contarle cosas distintas a la misma persona. Acá se les
             agrega lo único que un correo no puede tener, que es el botón para
             resolverlos. ---- */}
        {avisos.map((a) => (
          <div key={a.codigo} style={estiloAviso(a.severidad)} className="d-flex justify-content-between align-items-center">
            <div>
              <strong>{a.titulo}</strong>
              <div className="small">{a.detalle}</div>
              {ACCION_SOBRE_PLAN.includes(a.codigo) && pendiente?.plan && (
                <div className="small text-monospace" style={{ opacity: 0.75 }}>{pendiente.plan.planId}</div>
              )}
            </div>
            <div className="flex-shrink-0">
              {a.codigo === "breaker_abierto" && (
                <Boton tipo="primario" onClick={onReanudar}>Reanudar</Boton>
              )}
              {a.codigo === "sin_aplicar" && pendiente?.plan && (
                <Boton
                  tipo="primario"
                  onClick={() => aplicarPlan(pendiente.plan.planId, false)}
                  disabled={showLoading || breakerAbierto}
                >
                  Aplicar ahora
                </Boton>
              )}
            </div>
          </div>
        ))}

        {/* Un plan del día todavía sin aplicar no es un aviso —el atraso se
            cuenta en días— pero el botón tiene que estar a mano igual. */}
        {pendiente?.hayPlanSinAplicar && !avisos.some((a) => ACCION_SOBRE_PLAN.includes(a.codigo)) && (
          <div style={estiloAviso("media")} className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Plan del día sin aplicar</strong> — {resumenCorto(pendiente.plan.resumen)}.
              <div className="small text-monospace" style={{ opacity: 0.75 }}>{pendiente.plan.planId}</div>
            </div>
            <Boton
              tipo="primario" className="flex-shrink-0"
              onClick={() => aplicarPlan(pendiente.plan.planId, false)}
              disabled={showLoading || breakerAbierto || pendiente.plan.guardasOk === false}
            >
              Aplicar ahora
            </Boton>
          </div>
        )}

        {/* ---- Estado, en cuatro tarjetas iguales ---- */}
        <Row className="g-3 mb-3">
          <Col md="3">
            <Kpi
              titulo="Receptores vigentes"
              ayuda="Receptores activos según la última corrida (plan o aplicar) — no es una cuenta en vivo."
              valor={vigentes != null ? vigentes.toLocaleString("es-CL") : "—"}
            >
              <div style={AYUDA}>en la base de {ambiente}</div>
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Última descarga"
              ayuda="Cuándo se bajó por última vez el CSV del SII. La descarga sola no cambia nada en la base."
              valor={fecha(panel?.ultimaDescarga?.descargado_at)}
            >
              <div className="text-truncate" title={panel?.ultimaDescarga?.archivo || ""} style={AYUDA}>
                {panel?.ultimaDescarga?.archivo || "—"}
              </div>
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Última aplicación"
              ayuda="Cuándo se escribió por última vez en padron_receptores_sii. Es la fecha que dice qué tan viejo está el padrón de verdad."
              valor={
                <>
                  {fecha(pendiente?.ultimaAplicacion?.at)}
                  {pendiente?.ultimaAplicacion?.dias > 1 && (
                    <span className="ml-2">
                      <Etiqueta tono={pendiente.ultimaAplicacion.dias >= 3 ? "alerta" : "neutro"}>
                        hace {pendiente.ultimaAplicacion.dias} días
                      </Etiqueta>
                    </span>
                  )}
                </>
              }
            >
              {/* Sin ninguna aplicación previa el KPI ya dice «—»; agregarle
                  «sin resumen» debajo sugiere que hubo una aplicación de la que
                  falta el detalle, que es otra cosa. */}
              {pendiente?.ultimaAplicacion && (
                <div style={AYUDA}>{resumenCorto(pendiente.ultimaAplicacion.resumen)}</div>
              )}
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Estado del módulo"
              ayuda="Resume las dos banderas: el interruptor del operador (automático) y el circuit breaker (reacción a errores)."
              valor={<Etiqueta tono={estadoGeneral.tono} grande>{estadoGeneral.texto}</Etiqueta>}
            >
              <div style={AYUDA}>{estadoGeneral.nota}</div>
              <div style={AYUDA}>
                circuit breaker {breaker ? `${breaker.errores_consecutivos}/3` : "—"} · {breaker ? (breakerAbierto ? "abierto" : "cerrado") : "—"}
              </div>
            </Kpi>
          </Col>
        </Row>

        {/* ---- Controles, todos juntos y explicados ---- */}
        <Card className="mb-3">
          <Card.Body className="py-3">
            {/* Tres columnas iguales y con la misma forma: título, una fila de
                acción del mismo alto (FILA_ACCION) y la explicación debajo. */}
            <Row className="align-items-start">
              <Col md="4">
                <div style={ETIQUETA}>
                  Piloto automático
                </div>
                {/* Nada de <Form.Check type="switch">: este proyecto tiene
                    Bootstrap 4.6 con react-bootstrap 2.x, que emite el markup de
                    Bootstrap 5 (.form-switch) para el que el tema no trae NINGUNA
                    regla. El interruptor no se dibujaba —quedaba el hueco de 35px
                    del label y el texto capitalizado por el tema—, así que el
                    mando principal del módulo era invisible. Un estado + un botón
                    que dice qué va a pasar no depende de ese CSS y además es
                    menos ambiguo que un switch. */}
                <div className="d-flex align-items-center" style={FILA_ACCION}>
                  <Etiqueta tono={cronActivo ? "ok" : "neutro"} grande>
                    {cronActivo ? "prendido" : "apagado"}
                  </Etiqueta>
                  <Boton onClick={onToggleAutomatico} disabled={cargandoPanel || !panel}>
                    {cronActivo ? "Apagar" : "Prender"}
                  </Boton>
                </div>
                <div style={AYUDA}>
                  {cronActivo ? "Descarga y planifica solo, todos los días a las 04:00. " : "No dispara nada por su cuenta. "}
                  Prende y apaga <strong>solo la corrida diaria</strong>. Apagarlo no bloquea el botón de acá al
                  lado, y tampoco aplica nada: aplicar siempre lo aprieta una persona.
                  {!cronActivo && apagadoPor && <> Lo apagó {apagadoPor}.</>}
                </div>
              </Col>
              <Col md="4">
                <div style={ETIQUETA}>
                  Corrida manual
                </div>
                <div className="d-flex align-items-center" style={FILA_ACCION}>
                  <Boton tipo="primario" grande onClick={ejecutarIncremental} disabled={showLoading || breakerAbierto}>
                    {cfgAmbiente.boton}
                  </Boton>
                </div>
                <div style={AYUDA}>
                  Descarga el padrón y arma el plan. <strong>No escribe en la base</strong>: deja el plan listo para revisar.
                  {breakerAbierto && <> Hoy está bloqueado por el circuit breaker.</>}
                </div>
              </Col>
              <Col md="4">
                <div style={ETIQUETA}>
                  Circuit breaker
                </div>
                <div className="d-flex align-items-center" style={FILA_ACCION}>
                  <Etiqueta tono={breakerAbierto ? "error" : "ok"} grande>
                    {breakerAbierto ? "abierto" : "cerrado"}
                  </Etiqueta>
                  {breakerAbierto && <Boton tipo="primario" onClick={onReanudar}>Reanudar</Boton>}
                </div>
                <div style={AYUDA}>
                  Se abre solo, al tercer error seguido, y ahí bloquea todo —cron y botón— hasta que alguien lo reanude.
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ---- Cómo funciona: plegado, para que no estorbe al que ya lo sabe ---- */}
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center" style={{ gap: "16px" }}>
              <div style={{ fontSize: "13px", color: TEMA.suave }}>
                Cómo funciona y qué toca cada paso — los tres actos, cuál es automático y cuál lo aprieta una persona
              </div>
              <Boton className="flex-shrink-0" onClick={() => setVerComoFunciona((v) => !v)}>
                {verComoFunciona ? "Ocultar" : "Ver el diagrama"}
              </Boton>
            </div>
            {verComoFunciona && (
              <div className="mt-3">
                <DiagramaFlujo />
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ---- Historial y detalle, uno al lado del otro y con títulos ---- */}
        <Row className="g-3 mb-3">
          <Col lg="7">
            <Card className="h-100">
              <Card.Body>
                <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
                  Historial de ejecuciones — clic en una fila para ver el detalle
                </div>
                <Form.Control
                  type="text" placeholder="Filtrar por tipo, resultado o plan…" className="mb-2"
                  value={filter} onChange={(e) => setFilter(e.target.value)}
                />
                <DataTable
                  className="-striped -highlight"
                  fixedHeader
                  columns={columnas}
                  data={indiceFiltrado}
                  pagination
                  paginationPerPage={10}
                  highlightOnHover
                  pointerOnHover
                  onRowClicked={seleccionarFila}
                  progressPending={cargandoPanel}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col lg="5">
            <Card className="h-100">
              <Card.Body>
                <div style={{ ...ETIQUETA, marginBottom: "10px" }}>
                  Detalle del plan
                </div>
                <DetallePlan
                  fila={filaSeleccionada}
                  encabezado={planDetalle}
                  cargando={cargandoDetalle}
                  error={errorDetalle}
                  estado={estadoPlanSeleccionado}
                  breakerAbierto={breakerAbierto}
                  ocupado={showLoading || exportando}
                  onReintentar={() => seleccionarFila(filaSeleccionada)}
                  onAplicar={aplicarPlan}
                  onVerRegistros={(lista) => setRegistros({ show: true, lista })}
                  onExportar={exportarDetalleCSV}
                  onIrAPlan={seleccionarFila}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ---- Consulta puntual, al final: es la herramienta de soporte ---- */}
        <Card className="mb-3">
          <Card.Body>
            <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
              Buscar un receptor en el padrón completo
            </div>
            <div className="mb-2" style={AYUDA}>
              Busca en toda la base de {ambiente}, no solo en el plan que esté seleccionado arriba.
              Sirve para responder «¿a qué casilla le estamos mandando los DTE a este RUT?».
            </div>
            <Row>
              <Col lg="8">
                <BuscadorReceptorPadron getSession={getSession} />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      <RegistrosPlan
        show={registros.show}
        onHide={() => setRegistros({ show: false, lista: null })}
        getSession={getSession}
        planId={planDetalle?.plan_id}
        totales={planDetalle?.totales}
        listaInicial={registros.lista
          || LISTAS_PLAN.map((l) => l.lista).find((l) => planDetalle?.totales?.[l] > 0)}
        onExportar={exportarDetalleCSV}
        exportando={exportando}
      />
      <ModalCargando
        modalTitleLoading="Padrón SII"
        showLoading={showLoading}
        loadingDetail={true}
        loadingMessage={loadingMessage}
        handleClose={() => setShowLoading(false)}
      />
      <ModalError
        showTitle="Padrón SII"
        showErrorModal={showErrorModal}
        errorMessage={errorMessage}
        resetModal={() => setShowErrorModal(false)}
      />
    </>
  );
}

// Busca un receptor en el padrón COMPLETO (vía v_padron_receptores, con
// overrides aplicados) — independiente de cuál plan esté seleccionado, o de
// si hay alguno seleccionado. Reemplaza la búsqueda vieja que solo miraba el
// delta de un plan cargado en memoria.
function BuscadorReceptorPadron({ getSession }) {
  const [rut, setRut] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [noEncontrado, setNoEncontrado] = useState(false);

  const buscar = async (e) => {
    e.preventDefault();
    if (!rut) return;
    setBuscando(true);
    setResultado(null);
    setNoEncontrado(false);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await buscarReceptor(idToken, rut);
      setResultado(resp.data);
    } catch (error) {
      if (error?.response?.status === 404) setNoEncontrado(true);
      else console.error(error);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <>
      <Form onSubmit={buscar} className="d-flex mb-2" style={{ gap: "8px" }}>
        <Form.Control type="text" placeholder="RUT sin DV, ej: 12345678" value={rut} onChange={(e) => setRut(e.target.value)} />
        <Boton type="submit" tipo="primario" className="pbtn--campo" disabled={buscando}>{buscando ? <Spinner animation="border" size="sm" /> : "Buscar"}</Boton>
      </Form>
      {noEncontrado && <p className="text-muted small mb-0">No está en el padrón de esta base (o no vigente, o fuera del ambiente que esta base admite — ver la guarda de ambiente).</p>}
      {resultado && (
        <Table size="sm" borderless className="mb-0">
          <tbody>
            <tr><td className="text-muted">RUT</td><td>{resultado.rut}-{resultado.dv}</td></tr>
            <tr><td className="text-muted">Razón social</td><td>{resultado.razon_social}{resultado.tiene_override && <span className="ml-2"><Etiqueta tono="azul">con override</Etiqueta></span>}</td></tr>
            <tr><td className="text-muted">Mail intercambio</td><td>{resultado.mail_intercambio || <span className="text-muted">— ({resultado.mail_motivo || "sin dato"})</span>}{resultado.mail_es_override && <span className="ml-2"><Etiqueta tono="azul">override</Etiqueta></span>}</td></tr>
            <tr><td className="text-muted">Vigente</td><td>{resultado.vigente ? <Etiqueta tono="ok">sí</Etiqueta> : <Etiqueta tono="neutro">no{resultado.baja_at ? ` (baja ${resultado.baja_at.slice(0, 10)})` : ""}</Etiqueta>}</td></tr>
            <tr><td className="text-muted">Última actualización</td><td>{resultado.importado_at?.replace("T", " ").slice(0, 16)}</td></tr>
          </tbody>
        </Table>
      )}
    </>
  );
}

export default PadronSii;
